/**
 * NoorView data model.
 *
 * A masjid owns screens. Each screen shows a Design: a grid of widgets. Editors change a draft design in the
 * Studio and publish it; a published version is immutable, so screens always show a complete, consistent layout.
 */

export type Aspect = '16:9' | '9:16' | '21:9' | '4:3';

/**
 * Each screen shape has one fixed grid. Editors can't change it: widgets snap to these cells and are only resized in
 * whole cells. The grids are fine enough for precise layouts, and map exactly onto the templates' 12 × 8 and 6 × 10.
 */
export const ASPECTS: Record<Aspect, { width: number; height: number; cols: number; rows: number; label: string }> = {
  '16:9': { width: 1920, height: 1080, cols: 24, rows: 16, label: 'Landscape' },
  '9:16': { width: 1080, height: 1920, cols: 12, rows: 20, label: 'Portrait' },
  '21:9': { width: 2560, height: 1080, cols: 32, rows: 14, label: 'Ultrawide' },
  '4:3': { width: 1600, height: 1200, cols: 20, rows: 16, label: 'Classic' },
};

export type WidgetType =
  | 'prayerTimes'
  | 'clock'
  | 'jumuah'
  | 'countdown'
  | 'announcements'
  | 'posters'
  | 'donations'
  | 'donationQr'
  | 'ayah'
  | 'hadith'
  | 'events'
  | 'ticker'
  | 'logo'
  | 'iftar'
  | 'sehri'
  | 'tarawih'
  | 'heading'
  | 'paragraph'
  | 'makruh';

/** 'none' has no background or border at all: just the content, still placed on the grid. */
export type WidgetStyle = 'glass' | '3d' | 'flat' | 'legacy' | 'none';
export type TextCase = 'normal' | 'upper' | 'lower';
export type TextAlign = 'left' | 'center' | 'right';
export type FontWeight = 400 | 500 | 600 | 700 | 800;

export interface TextOptions {
  font: string;
  weight: FontWeight;
  /** Base size in points at 1080p. Other text in the widget scales relative to it. */
  size: number;
  align: TextAlign;
  case: TextCase;
  /** Scale text with the widget's size, so a bigger widget gets bigger text. */
  scale: boolean;
}

export interface Widget<P = Record<string, unknown>> {
  id: string;
  type: WidgetType;
  /** Grid position (0-based column and row) and size in cells. */
  x: number;
  y: number;
  w: number;
  h: number;
  name?: string;
  hidden?: boolean;
  locked?: boolean;
  style: WidgetStyle;
  /** Highlight colour (next prayer, ticker tag, progress bar), #RRGGBB. */
  accent: string;
  /** The widget's own colour, #RRGGBB. Empty uses the style's standard colour. */
  fill?: string | null;
  /** Text colour, #RRGGBB. Empty picks white or dark automatically, whichever reads best. */
  textColor?: string | null;
  /** Border drawn inside the widget's edge, so it never changes the spacing. Width in px at the native resolution, 0–20. */
  border?: { width: number; color: string } | null;
  /** Background opacity, 0–100. */
  opacity: number;
  /**
   * Content size: everything inside the widget scales together, as a percentage of the largest size that fits
   * (100 fills the widget up to a small inner margin). Empty means automatic, from the text size.
   */
  fit?: number | null;
  /** Corner radius in px at the native resolution (0–64). Empty uses the screen's standard curve. */
  radius?: number | null;
  text: TextOptions;
  props: P;
}

export interface Background {
  kind: 'solid' | 'gradient' | 'image';
  color: string;
  color2?: string;
  image?: string;
  /** An Islamic geometric pattern drawn over the background (full screen, as a border, or in the corners). */
  pattern?: import('./patterns').BackgroundPattern | null;
}

export interface Design {
  schema: 1;
  aspect: Aspect;
  /** Always the aspect's fixed grid (see ASPECTS). */
  cols: number;
  rows: number;
  /** Spacing between widgets, 0–0.5 (0.06 small, 0.12 medium, 0.2 large). */
  gap: number;
  /** Safe border around the whole screen, as a fraction of its shorter side. Never zero, so widgets never touch the edge. */
  margin: number;
  background: Background;
  /** Widgets in paint order: the last one sits in front. */
  widgets: Widget[];
}

/* ---------- prayer settings ---------- */

export const PRAYERS = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'] as const;
export type Prayer = (typeof PRAYERS)[number];
export const SALAH = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const;
export type Salah = (typeof SALAH)[number];

export type CalcMethod =
  | 'MoonsightingCommittee'
  | 'MuslimWorldLeague'
  | 'Egyptian'
  | 'Karachi'
  | 'UmmAlQura'
  | 'Dubai'
  | 'Qatar'
  | 'Kuwait'
  | 'Singapore'
  | 'Turkey'
  | 'Tehran'
  | 'NorthAmerica';

export type HighLatitudeRule = 'MiddleOfTheNight' | 'SeventhOfTheNight' | 'TwilightAngle';

export interface JamaahRule {
  /** 'offset': minutes after the start time. 'fixed': a set time each day. 'none': no jama'ah shown. */
  mode: 'offset' | 'fixed' | 'none';
  minutes?: number;
  time?: string; // HH:MM
  /** Round the result up to this many minutes (e.g. 5 → 13:12 becomes 13:15). */
  roundTo?: number;
}

export interface JumuahSlot {
  label: string;
  khutbah: string; // HH:MM
  jamaah: string; // HH:MM
}

export interface EidSalah {
  id: string;
  name: string; // e.g. "Eid al-Adha"
  date: string; // YYYY-MM-DD
  takbeer?: string; // HH:MM
  jamaats: string[]; // HH:MM list
}

/** When screens blank for a salah: how long after the jama'ah time, and for how long. */
export interface SalahBlankRule {
  on: boolean;
  /** Seconds after the jama'ah time (15 s – 20 min). */
  delaySec: number;
  /** Minutes the screen stays blank. */
  minutes: number;
}
export type SalahBlankKey = Salah | 'jumuah' | 'eid';

/**
 * Salah mode: during each jama'ah the screens go blank apart from a small digital clock at the top, then return to
 * their normal design by themselves. Screens work this out on their own, so it happens even without internet.
 */
export interface SalahMode {
  enabled: boolean;
  clock: 'top-left' | 'top-center' | 'top-right';
  h12: boolean;
  prayers: Record<SalahBlankKey, SalahBlankRule>;
}

export interface PrayerSettings {
  method: CalcMethod;
  madhab: 'Shafi' | 'Hanafi';
  highLatitudeRule: HighLatitudeRule;
  /** Minutes to add to each calculated start time. */
  adjustments: Partial<Record<Prayer, number>>;
  jamaah: Record<Salah, JamaahRule>;
  jumuah: JumuahSlot[];
  eid: EidSalah[];
  /** Days to add to the calculated Hijri date (local moon sighting), -2…2. */
  hijriAdjust: number;
  salahMode: SalahMode;
}

export interface Masjid {
  id: string;
  name: string;
  /** IANA time zone, e.g. Europe/London. All times are shown in this zone, whatever the device says. */
  timezone: string;
  latitude: number;
  longitude: number;
  city?: string;
  prayer: PrayerSettings;
  plan: 'free' | 'basic' | 'pro';
  /** The masjid's logo (an uploaded image URL), shown by the Logo widget. */
  logo?: string | null;
  /** The masjid's own colours (its branding), offered first in every colour choice in the Studio. Up to 8. */
  colors?: string[];
  /** Fingerprint of the display exit PIN (see pin.ts). Empty: locked displays can only be released by unpairing. */
  exitPinHash?: string | null;
  /** The Live Khutbah add-on (switched on by Noor MS), and its settings. */
  khutbahAddon?: boolean;
  khutbah?: import('./khutbah').KhutbahSettings | null;
}

/** One day's times from the masjid's uploaded timetable (the only source of prayer times). */
export interface TimetableDay {
  date: string; // YYYY-MM-DD
  fajr?: string;
  sunrise?: string;
  dhuhr?: string;
  asr?: string;
  maghrib?: string;
  isha?: string;
  fajrJamaah?: string;
  dhuhrJamaah?: string;
  asrJamaah?: string;
  maghribJamaah?: string;
  ishaJamaah?: string;
  /** Friday jama'ahs (first and second Jumu'ah). */
  jumuah1?: string;
  jumuah2?: string;
  /** Ramadan: when sehri ends, and Tarawih. Left blank the rest of the year. */
  sehri?: string;
  tarawih?: string;
}

/* ---------- screens ---------- */

export type PowerState = 'on' | 'off' | 'idle';

export interface Screen {
  id: string;
  masjidId: string;
  name: string;
  /** The design this screen shows normally. */
  designId: string;
  /** Device currently paired to this screen, if any. */
  deviceId?: string | null;
  power: PowerState;
  /** When locked, nothing new is sent live to this screen until it is unlocked. */
  locked: boolean;
  /** Whether this screen blanks during salah (when Salah mode is on). Defaults to yes. */
  salahBlank?: boolean;
  lastSeenAt?: string | null;
  /** Version the device reported it is showing. */
  showingVersion?: number | null;
  /** What the paired display is, e.g. "Fire TV · AFTKA" or "Chrome on Windows" (see deviceLabel). */
  deviceName?: string | null;
  createdAt: string;
}

export interface DesignRecord {
  id: string;
  masjidId: string;
  name: string;
  draft: Design;
  draftUpdatedAt: string;
  /** Bumped on every save; used to detect two people editing at once. */
  draftRevision: number;
  publishedVersion: number;
  published?: Design | null;
  publishedAt?: string | null;
}

/** A layout an editor saved to reuse ("My templates"). */
export interface SavedTemplate {
  id: string;
  masjidId: string;
  name: string;
  design: Design;
  createdAt: string;
}

export interface Schedule {
  id: string;
  masjidId: string;
  screenId: string;
  /** Design shown during the window instead of the screen's normal design. */
  designId: string;
  title: string;
  startsAt: string; // ISO instant
  durationMinutes: number;
  repeatWeekly: boolean;
  createdAt: string;
}

export interface ActivityItem {
  id: string;
  masjidId: string;
  kind: 'screen-offline' | 'screen-online' | 'published' | 'timetable' | 'schedule' | 'eid' | 'paired' | 'settings' | 'salah' | 'message' | 'appeal' | 'khutbah' | 'team';
  title: string;
  detail: string;
  at: string;
  read: boolean;
}

/** Everything a screen needs to run for days without a connection. */
export interface ScreenBundle {
  screen: Pick<Screen, 'id' | 'name' | 'power' | 'locked' | 'salahBlank'>;
  masjid: Masjid;
  version: number;
  design: Design;
  /** Scheduled alternatives, resolved on the device by time. */
  schedules: Array<{ id: string; startsAt: string; durationMinutes: number; repeatWeekly: boolean; design: Design }>;
  timetable: TimetableDay[];
  /** Live messages and appeals for this screen (current and upcoming). */
  broadcasts?: import('./broadcast').Broadcast[];
  generatedAt: string;
}
